import { memo, useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { Grid2, Box, CircularProgress, Button, Typography, Stack } from '@mui/material';
import { DataGridPro, type GridColDef, useGridApiRef, gridVisibleColumnFieldsSelector, type GridSortModel } from '@mui/x-data-grid-pro';
import { useCommonStyles } from '../../styles/GenericStyle';
import { useResponsiveHeaderHeight } from '../../hooks/useResponsiveHeaderHeight';
import { useEditTrialMaterials, useGetMaterialByTrial } from '../../api/vegApl';
import { getMaterialsByTrial as fetchAllMaterialsByTrial } from '../../api/vegApl/vegApl';
import { getTrialMaterialGridCols } from "./trailGridConfigs";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { trailMaterialsGridColumns, LOCAL_STORAGE_KEYS, DRILLDOWN_DEFAULT_PAGE_SIZE as DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, TRAIL_MATERIALS_PINNED_LEFT_COLUMNS } from "./trialConstants";
import type { TrialMaterial, ColumnVisibility, TrailMaterialsDataGridProps, PaginationModel, TrialMatReq } from "./trailInterface";
import { useCommonTrailStyles, getBaseDataGridSx } from "./trailStyles";
import { CustomizeColumnsPanel } from "../common/CustomizeColumnsPanel";
import { GlobalStateContext, useGlobalState } from '../../contexts/GlobalState';
import { useSnackbar } from '../../contexts/SnackbarContext';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterButton from '../common/FilterButton';
import GenericFilterPanel from '../common/GenericFilterPanel';
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import { usePersistedGridState } from '../../hooks/usePersistedGridState';
import ShowFilterChips from '../common/ShowFilterChips';
import {checkArray } from '../../utils/HelperFunction';
import { buildTrialMaterialsExportData } from '../../utils/ExcelExport';
import { mapMaterialWithHarvestRows } from './trailMaterialRowMapper';
import { useTrialMaterialBulkActions } from './useTrialMaterialBulkActions';
import TrialMaterialBulkActions from './TrialMaterialBulkActions';

const TRIAL_MATERIAL_SORT_FIELD_MAP: Record<string, string> = {
    current_status: 'material_status',
    required_grams: 'required_seed_weight_g',
    required_fruit: 'required_fruit_count',
};

function TrailMaterialsDataGrid({ data, setToggleMaterialDetails, exportToExcel, onUnsavedChangesUpdate, onBackToTrials, toggleMaterialDetails, hidden, activeTab, isArchived = false, drilldownVisitId = 0 }: TrailMaterialsDataGridProps) {
    const classes = useCommonStyles();
    const headerHeight = useResponsiveHeaderHeight(88);
    const apiRef = useGridApiRef();
    const { applyColumnWidths, onColumnWidthChange, clearColumnWidths } = usePersistedGridState('trial-materials-grid');
    const trailClasses = useCommonTrailStyles();
    const { trialRemarks, materialStatuses, priorities } = useContext(GlobalStateContext);
    const { showSnackbar } = useSnackbar();
    const { state: { roleSelected = '', user_id_db } } = useGlobalState();
    const canEditTrialDrilldown = useMemo(() => {
        const normalizedRole = String(roleSelected ?? '').trim();
        return normalizedRole === 'Admin' || normalizedRole === 'S2S-Lead';
    }, [roleSelected]);
    const [isLoading, setIsLoading] = useState(false);
    const [trialGridData, setTrialGridData] = useState<TrialMaterial[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => {
        const savedVisibility = localStorage.getItem(LOCAL_STORAGE_KEYS.TRAIL_MATERIALS_COLUMN_VISIBILITY);
        if (savedVisibility) {
            const parsed = JSON.parse(savedVisibility) as Partial<ColumnVisibility>;
            return (Object.keys(trailMaterialsGridColumns) as Array<keyof typeof trailMaterialsGridColumns>).reduce<ColumnVisibility>((acc, key) => {
                acc[key] = parsed[key] ?? trailMaterialsGridColumns[key];
                return acc;
            }, {});
        }
        return trailMaterialsGridColumns;
    });
    const [columnOrder, setColumnOrder] = useState<string[]>(() => {
        const savedOrder = localStorage.getItem(LOCAL_STORAGE_KEYS.TRAIL_MATERIALS_COLUMN_ORDER);
        return savedOrder ? JSON.parse(savedOrder) : [];
    });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({});
    const [showCustomizeColumns, setShowCustomizeColumns] = useState(false);
    const [expandedParentRows, setExpandedParentRows] = useState<Record<string, boolean>>({});

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, []);
    const [paginationModel, setPaginationModel] = useState<PaginationModel>({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
    const [isFilterVisible, setFilterVisible] = useState(false);
    const isFilterActiveRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const listValues = useMemo(() => ({
        pollinationTypes: [] as any[],
        hrvmdList: [] as any[],
        priorityArr: [
            { id: 'High', value: 'High' },
            { id: 'Medium', value: 'Medium' },
            { id: 'Low', value: 'Low' },
        ],
        matStatusList: materialStatuses.map(s => ({ id: s.status_value, value: s.status_value })),
    }), [materialStatuses]);
    const [filterValues, setFilterValues] = useState<any[]>([]);
    const [filterDel, setFilterDel] = useState<Record<string, any>>({});
    const handleFilterSetGridData = useCallback((materials: any[]) => {
        const mapped = (materials ?? []).map((m: any, i: number) => mapMaterialWithHarvestRows(m, paginationModel.page, i, materialStatuses));    
        setTrialGridData(mapped);
        isFilterActiveRef.current = true;
    }, [paginationModel.page, materialStatuses]);

    const handleFilterApplied = useCallback((filterData: any | null) => {
        isFilterActiveRef.current = !!filterData;
    }, []);

    // Build sort params expected by the trial-materials API from the grid's sort model
    const buildSortParams = useCallback((model: GridSortModel): { sort_by: string | null; sort_order: 'ASC' | 'DESC' | null } => {
        if (!model || model.length === 0 || !model[0]?.sort) {
            return { sort_by: null, sort_order: null };
        }
        const sortItem = model[0];
        return { sort_by: TRIAL_MATERIAL_SORT_FIELD_MAP[sortItem.field] ?? sortItem.field, sort_order: sortItem.sort === 'asc' ? 'ASC' : 'DESC' };
    }, []);
    const sortParams = buildSortParams(sortModel);

    const { mutate: getMaterialsByTrial } = useGetMaterialByTrial({ trial_bid: data?.trial_bid, ...paginationModel, sort_by: sortParams.sort_by, sort_order: sortParams.sort_order }, {
        mutation: {
            onSuccess: (data: any) => {
                const materials = (data?.data?.materials ?? []).map((m: any, i: number) => mapMaterialWithHarvestRows(m, paginationModel.page, i, materialStatuses));
             
                setTrialGridData(materials);
                // Keep all parent rows collapsed by default.
                setExpandedParentRows({});
                setTotalRows(data?.data?.pagination?.total_records ?? 0);
                setIsLoading(false);

                // showSnackbar({ message: `Successfully loaded ${data?.data?.materials?.length ?? 0} materials`, severity: 'success' });
            },
            onError: (error: any) => {
                console.error('Failed to fetch trial materials:', error);
                setTrialGridData([]);
                setTotalRows(0);
                setIsLoading(false);

                showSnackbar({ message: 'Failed to fetch trial materials. Please try again.', severity: 'error' });
            }
        }
    });

    const { mutate: editMaterials } = useEditTrialMaterials({
        mutation: {

            onSuccess: (_) => {
                setIsSubmitting(false);
                //const results = (respose?.data as any)?.results || [];
                //const successCount = results?.filter((r: any) => r.status == 'success')?.length ?? 0;
                showSnackbar({ message: `Materials updated successfully`, severity: 'success' });
                fetchTrials();
            },
            onError: (error) => {
                setIsSubmitting(false);
                console.error('Error updating materials:', error);
                showSnackbar({ message: 'Failed to update materials. Please try again.', severity: 'error' });
            }
        }
    });

    const fetchTrials = useCallback(() => {
        setIsLoading(true);
        getMaterialsByTrial({ trialBid: data?.trial_bid, ...paginationModel, sort_by: sortParams.sort_by, sort_order: sortParams.sort_order });
    }, [getMaterialsByTrial, data?.trial_bid, paginationModel, sortParams.sort_by, sortParams.sort_order]);

    const {
        selectionModel,
        setSelectionModel,
        openRemarkDialog,
        setOpenRemarkDialog,
        selectedBulkRemark,
        setSelectedBulkRemark,
        bulkRemarkError,
        setBulkRemarkError,
        isBulkRemarkSubmitting,
        isManualCompleteSubmitting,
        selectedMaterialIds,
        canMarkSelectedAsComplete,
        clearBulkSelection,
        handleOpenUpdateRemark,
        handleSaveBulkRemark,
        handleMarkAsComplete,
    } = useTrialMaterialBulkActions({
        trialGridData,
        fetchTrials,
    });

    // Reset state when a new trial is selected so stale data/filters don't persist
    useEffect(() => {
        setTrialGridData([]);
        isFilterActiveRef.current = false;
        setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
        setFilterValues([]);
    }, [data?.trial_bid]);

    useEffect(() => {
        //console.log("isFilterActiveRef.current",isFilterActiveRef.current);
        if (activeTab === 2 && paginationModel.page != null && !toggleMaterialDetails && !isFilterActiveRef.current && data?.trial_bid) {
            fetchTrials();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleMaterialDetails, paginationModel, sortModel, data?.trial_bid]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setColumnVisibility((prev) => ({ ...prev, [name]: checked }));
    }, []);

    const getHarvestOptAvailable = useCallback((params: any) => {
        const row = params?.row ?? {};
        return row._rowType !== 'child' && !row._isNoDataRow;
    }, []);


    useEffect(() => {
        const defaultColDefs = getTrialMaterialGridCols(classes, trailClasses, materialStatuses, trialRemarks, handleRowUpdate, sortModel, isArchived, roleSelected, priorities);
        const visibilityModel: Record<string, boolean> = {};

        for (const column of defaultColDefs) {
            if (column.field) {
                if (isArchived) {
                    // In archived view, show all columns as non-editable (column defs handle read-only rendering)
                    visibilityModel[column.field] = true;
                } else {
                    const vis = columnVisibility[column.headerName as keyof ColumnVisibility];

                    // Only include explicit booleans; omit undefined so DataGrid defaults to visible
                    if (typeof vis === 'boolean') {
                        visibilityModel[column.field] = vis;
                    }
                }
            }
        }
        setColumnVisibilityModel(visibilityModel);

    }, [columnVisibility, classes, trailClasses, materialStatuses, trialRemarks, sortModel, isArchived, roleSelected, priorities]);

    const handleRowUpdate = useCallback((field: string, value: any, params: any) => {

        const parentUid = params?.row?._rowUid;
        if (params?.row?._rowType == "parent") {
            setTrialGridData((prevData: any[]) => {
                return prevData.map((row: any) =>
                    row?._rowUid === parentUid
                        ? { ...row, [field]: value, isModified: true }
                        : row
                );
            });
        }
        else {
            setTrialGridData((prevData: any[]) => {
                return prevData.map((p: any) => ({
                    ...p,
                    _childRows: p?._childRows.map((c: any) =>
                        c._rowUid === parentUid
                            ? { ...c, [field]: value, isModified: true }
                            : c

                    )
                }));
            });
        }
        // Update parent aggregate only for numeric child fields.
        const aggregateFields = ['fruit_weight_g', 'fruit_count', 'seed_count', 'seed_weight_g', 'bags'];
        if (!aggregateFields.includes(field)) {
            return;
        }

        //update sum of fields
        let parent = params?.row?._parentRowUid;
        setTrialGridData((prevData: any) => {

            const childrenOfParent = prevData
                .flatMap((p: any) => p._childRows || [])
                .filter((c: any) => c._parentRowUid === parent);

            const totalVal = childrenOfParent.reduce(
                (sum: any, c: any) => sum + (isNaN(c[field]) || !c[field] ? 0 : parseInt(c[field])),
                0
            );
            return prevData.map((row: any) =>
                row?._rowUid === parent
                    ? { ...row, [field]: totalVal }
                    : row
            );
        });

    }, [setTrialGridData]);

    // Report unsaved changes to parent
    useEffect(() => {
        const hasUnsavedChanges = trialGridData.some(material => material._rowType !== 'child' && material.isModified);
        onUnsavedChangesUpdate?.(hasUnsavedChanges);
    }, [trialGridData, onUnsavedChangesUpdate]);

    const visibleGridRows = useMemo(() => {
        const rows: any[] = [];
        for (const parentRow of trialGridData) {
            const parentRowUid = parentRow?._rowUid ?? '';
            rows.push({
                ...parentRow,
                _isExpanded: !!expandedParentRows[parentRowUid],
                _onToggleExpand: (rowUid: string) => {
                    setExpandedParentRows((prev) => ({ ...prev, [rowUid]: !prev[rowUid] }));
                }
            });
            if (parentRowUid && expandedParentRows[parentRowUid] && Array.isArray(parentRow._childRows) && parentRow._childRows.length > 0) {
                rows.push(...parentRow._childRows);
            }
        }
        return rows;
    }, [trialGridData, expandedParentRows]);

    const expandableParentRowUids = useMemo(() => {
        return trialGridData
            .filter((row: any) => row?._rowType === 'parent' && row?._hasChildren)
            .map((row: any) => row._rowUid)
            .filter(Boolean);
    }, [trialGridData]);

    const hasExpandableRows = expandableParentRowUids.length > 0;

    const areAllExpanded = useMemo(() => {
        if (!hasExpandableRows) return false;
        return expandableParentRowUids.every((rowUid: string) => !!expandedParentRows[rowUid]);
    }, [expandableParentRowUids, expandedParentRows, hasExpandableRows]);

    const toggleAllExpanded = useCallback(() => {
        if (!hasExpandableRows) return;

        setExpandedParentRows((prev) => {
            const shouldExpandAll = !expandableParentRowUids.every((rowUid: string) => !!prev[rowUid]);
            const nextState: Record<string, boolean> = {};
            expandableParentRowUids.forEach((rowUid: string) => {
                nextState[rowUid] = shouldExpandAll;
            });
            return nextState;
        });
    }, [expandableParentRowUids, hasExpandableRows]);

    const gridColumns = useMemo((): GridColDef[] => {
        const defaultColDefs = getTrialMaterialGridCols(
            classes,
            trailClasses,
            materialStatuses,
            trialRemarks,
            isArchived ? undefined : handleRowUpdate,
            sortModel,
            isArchived,
            roleSelected,
            priorities,
            toggleAllExpanded,
            areAllExpanded,
            hasExpandableRows,
        );

        let orderedColumns = [...defaultColDefs];
        if (columnOrder.length > 0) {
            orderedColumns = columnOrder
                .map(field => defaultColDefs.find(col => col.field === field))
                .filter((col): col is GridColDef => col !== undefined);
            const orderedFields = new Set(columnOrder);
            const newColumns = defaultColDefs.filter(col => !orderedFields.has(col.field));
            orderedColumns = [...orderedColumns, ...newColumns];
        }

        // Ensure expand arrow stays at the start of table
        const expandColIndex = orderedColumns.findIndex(col => col.field === '__expand__');
        if (expandColIndex > 0) {
            const [expandCol] = orderedColumns.splice(expandColIndex, 1);
            orderedColumns.unshift(expandCol);
        }

        // Keep Material ID immediately after expand column
        const matIdIndex = orderedColumns.findIndex(col => col.field === 'Material ID');
        if (matIdIndex > 1) {
            const [matIdCol] = orderedColumns.splice(matIdIndex, 1);
            orderedColumns.splice(1, 0, matIdCol);
        }

        return orderedColumns;
    }, [classes, trailClasses, materialStatuses, trialRemarks, columnOrder, handleRowUpdate, sortModel, isArchived, roleSelected, priorities, toggleAllExpanded, areAllExpanded, hasExpandableRows]);

    const handleResetToDefault = useCallback(() => {
        setColumnVisibility(trailMaterialsGridColumns);
        setColumnOrder([]);
        clearColumnWidths();
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TRAIL_MATERIALS_COLUMN_VISIBILITY);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TRAIL_MATERIALS_COLUMN_ORDER);
    }, [clearColumnWidths]);

    const handleBackToTrials = useCallback(() => {
        onBackToTrials?.();
        setToggleMaterialDetails(true);
    }, [setToggleMaterialDetails, onBackToTrials]);
    const handleToggleCustomize = useCallback(() => setShowCustomizeColumns(prev => !prev), []);

    const handleColumnOrderChange = useCallback(() => {
        const visibleColumnFields = gridVisibleColumnFieldsSelector(apiRef);
        setColumnOrder(visibleColumnFields);
        localStorage.setItem(LOCAL_STORAGE_KEYS.TRAIL_MATERIALS_COLUMN_ORDER, JSON.stringify(visibleColumnFields));
    }, [gridColumns, columnVisibilityModel]);

    const saveColumnPreferences = useCallback(() => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.TRAIL_MATERIALS_COLUMN_VISIBILITY, JSON.stringify(columnVisibility));
        localStorage.setItem(LOCAL_STORAGE_KEYS.TRAIL_MATERIALS_COLUMN_ORDER, JSON.stringify(columnOrder));
        setShowCustomizeColumns(false);
    }, [columnVisibility, columnOrder]);

    const saveMaterialInTrial = useCallback(() => {
        const materialsToUpdate: TrialMatReq[] = getParentChildUpdatedData(trialGridData);
        if (materialsToUpdate.length === 0) {
            showSnackbar({ message: 'No materials to update', severity: 'info' });
            return;
        }
        let remarkArray = trialRemarks;

        const transformedData = materialsToUpdate.map(({ mat_id, pollination_id, barcode, material_id, processing_priority,
            required_grams, required_fruit, required_seed_count, remarks, _childRows }) => ({
                trial_bid: data?.trial_bid,
                matid: mat_id,
                pollination_id,
                barcode,
                material_id,
                processing_priority,
                required_seed_grams: required_grams,
                required_fruit,
                required_seed_count,
                remark_id: (remarkArray.find((r: any) => r.remark_name === remarks)?.remark_id) ?? (remarks ? remarks : 0),
                harvests: _childRows.map(({
                    material_harvest_id, fruit_weight_g,
                    fruit_count,
                    bags }) => ({
                        material_harvest_id,
                        fruit_weight: fruit_weight_g,
                        fruit_count,
                        bags
                        // bags: bag_number


                    }))
            }));

     



        // const trialBody = materialsToUpdate.map(material => ({
        //     trial_bid: material.trial_bid ?? data?.trial_bid ?? '',
        //     matid: material['Material ID'] || material.matid || '',
        //     pollination_id: material.pollination_id ?? '',
        //     barcode: material.barcode ?? '',
        //     special_requisites: material.special_requisites ?? '',
        //     local_code: material.local_code ?? '',
        //     processing_priority: material.processing_priority ?? '',
        //     plants_to_harvest: material.plants_to_harvest ?? '',
        //     required_seed_grams: Number(material['Required Seed Weight (g)'] ?? material.required_seed_grams) || 0,
        //     required_fruit_weight: Number(material['Required Fruit Weight'] ?? material.required_fruit_weight) || 0,
        //     required_seed_count: Number(material['Required Seed Count'] ?? material.required_seed_count) || 0,
        // }));
        setIsSubmitting(true);

        editMaterials({ data: { trial_body: transformedData, user_id: Number(user_id_db ?? 0) } });
    }, [trialGridData, editMaterials, showSnackbar, data?.trial_bid, trialRemarks, user_id_db]);

    useEffect(() => {
        // const timer = setTimeout(() => {
        //     apiRef && apiRef?.current && apiRef?.current?.resize();
        // }, 500);
        if (toggleMaterialDetails) {           
            isFilterActiveRef.current = false;
            setFilterValues([]);
            setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
            setSelectionModel([]);
        }
         //return () => clearTimeout(timer);
    }, [hidden, toggleMaterialDetails]);

    const getParentChildUpdatedData = (trialGridData: any) => {
        const modifiedData = trialGridData
            .map((parent: any) => ({
                ...parent,
                _childRows: parent?._childRows.filter((child: any) => child.isModified === true)
            }))

            .filter(
                (parent: any) =>
                    parent.isModified === true ||
                    parent?._childRows.length > 0
            );

        return modifiedData;
    }

    const handleSetFilterValues = useCallback((data: any[]) => {
        setFilterValues(data);
    }, [setFilterValues]);

    const handleDeleteFilter = useCallback((filter: any) => {

        setFilterDel(filter);
    }, [])

    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = useCallback(async () => {
        if (!data?.trial_bid) return;
        try {
            setIsDownloading(true);
            const response = await fetchAllMaterialsByTrial({ trial_bid: data.trial_bid, page: 0, pageSize: Math.max(totalRows, DEFAULT_PAGE_SIZE) }) as any;
            const materials = response?.data?.materials ?? [];
            const exportData = buildTrialMaterialsExportData(materials, undefined, { trialRemarks, priorities });
            exportToExcel(exportData, `TrialMaterials_${data.trial_bid}`, ['isModified', 'material_id', 'status_id', '_rowUid', '_rowType', '_hasChildren', '_childRows']);
        } catch (error) {
            console.error('Failed to download trial materials:', error);
        } finally {
            setIsDownloading(false);
        }
    }, [data?.trial_bid, totalRows, trialRemarks, priorities, exportToExcel]);

    return (
        <>
            <GenericFilterPanel
                key={`trial-filter-${data?.trial_bid}-${drilldownVisitId}`}
                moduleName="trial_drill"
                listValues={listValues}
                setGridData={handleFilterSetGridData}
                paginationModel={paginationModel}
                setTotalRows={setTotalRows}
                onFilterApplied={handleFilterApplied}
                onRefreshData={fetchTrials}
                isFilterVisible={isFilterVisible}
                setFilterVisible={setFilterVisible}
                setFilterFieldData={handleSetFilterValues}
                filtetToDelete={filterDel}
                extraContext={{ trial_bid: data?.trial_bid }}
                persistState={false}

            />
            {(!toggleMaterialDetails && !hidden) && isLoading ? (
                <Box className={trailClasses.loadingContainer}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <>

                    <Box sx={{ display: toggleMaterialDetails || hidden ? 'none' : 'flex', px: { xs: 1, sm: 0 } }} >
                        <Typography component="div" sx={{ mt: { xs: 2, sm: 3, md: 2 }, cursor: "pointer" }} className={classes.labelNoto16Bold600} onClick={() => { handleBackToTrials() }} >
                            <ArrowBackIcon sx={{
                                verticalAlign: "middle", transform: 'scaleX(1.3)',
                                transformOrigin: 'center left',
                                marginRight: 1,
                                fontSize: { xs: '1.2rem', sm: '1.5rem' },
                            }} />Back to Trials
                        </Typography>
                    </Box>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12 }} sx={{ mt:1, p: 0, display: toggleMaterialDetails || hidden ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: { xs: 'calc(100vh - 160px)', sm: 'calc(100vh - 180px)', md: 'calc(100vh - 180px)' } }}>
                        <Box className={trailClasses.materialHeaderContainer} sx={{ padding: { xs: '8px', sm: '10px' }, flexShrink: 0, position: 'relative', zIndex: 1 }}>
                            <Box className={trailClasses.materialHeaderActionsBox} sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
                                <Stack className={trailClasses.materialHeaderLeftSection} sx={{ width: { xs: '100%', md: 'auto' }, minWidth: 0 }}>
                                    <Typography className={classes.labelNoto24Bold400} sx={{ fontSize: { xs: '16px', sm: '20px', md: '24px' }, wordBreak: 'break-word' }}>
                                        Trial Materials: {data?.trial_bid ?? ''}
                                    </Typography>
                                    <Typography className={trailClasses.materialInfoText}>
                                        {totalRows} Materials
                                    </Typography>
                                </Stack>
                                <Box className={trailClasses.flexRowWithGap} sx={{ alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                                    <Box sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 }, alignItems: 'center' }}>
                                        <FilterButton isFilterVisible={isFilterVisible} setFilterVisible={setFilterVisible} />
                                        <Button variant="contained" disableRipple className={classes.whiteButton} startIcon={<SettingsOutlinedIcon />}

                                            onClick={handleToggleCustomize} sx={{ ml: { xs: 0.5, sm: 1 }, fontSize: { xs: '11px', sm: '13px' }, whiteSpace: 'nowrap' }}>Customize columns</Button>
                                        <CustomizeColumnsPanel show={showCustomizeColumns} columnVisibility={columnVisibility}
                                            onColumnChange={handleChange} onResetToDefault={handleResetToDefault} saveColumnPreferences={saveColumnPreferences} />
                                    </Box>

                                    <Button variant="contained" disableRipple className={classes.whiteButton} startIcon={<FileDownloadOutlinedIcon />}
                                        onClick={handleDownload}
                                        disabled={isDownloading}
                                    >{isDownloading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}Download</Button>

                                    {!isArchived && canEditTrialDrilldown && <Button disableRipple variant="contained" className={classes.greenButton}
                                        onClick={() => saveMaterialInTrial()} loading={isSubmitting}
                                        loadingPosition="start"
                                    >Save</Button>}
                                </Box>
                            </Box>
                        </Box>


                        <Grid2 size={{ xs: 12, sm: 6, md: 12 }} sx={{ minHeight: showCustomizeColumns ? '500px' : 'auto' }}>
                            <Box sx={{ mt: 1 }}>
                                {filterValues.length > 0 && <ShowFilterChips filterValues={filterValues} onDelete={handleDeleteFilter} />}

                            </Box>
                            {  !(toggleMaterialDetails || hidden) && <Box>
                                <DataGridPro
                                    key={`materials-grid-${columnOrder.join('-')}`}
                                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                                    apiRef={apiRef}
                                    columns={applyColumnWidths(gridColumns)}                                  
                                    rows={checkArray(visibleGridRows)} 
                                    columnHeaderHeight={headerHeight}
                                    rowHeight={48}
                                    rowCount={totalRows}
                                    disableRowSelectionOnClick
                                    getRowId={(r) => r._rowUid}
                                    getRowClassName={(params) => {
                                        if (params.row?._isNoDataRow) return 'trail-empty-child-row';
                                        if (params.row?._rowType === 'child') return 'trail-child-row';
                                        return 'trail-parent-row';
                                    }}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnOrderChange={handleColumnOrderChange}
                                    sortingMode="server"
                                    sortModel={sortModel}
                                    onSortModelChange={handleSortModelChange}
                                    onColumnWidthChange={onColumnWidthChange}
                                    paginationMode="server"
                                    pagination
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    localeText={{
                                        noRowsLabel: 'No Materials found',
                                        noResultsOverlayLabel: 'No Materials found',
                                    }}
                                    initialState={{
                                        pinnedColumns: {
                                            left: [...TRAIL_MATERIALS_PINNED_LEFT_COLUMNS],
                                        },
                                        sorting: { sortModel: [{ field: 'trial_bid', sort: 'asc' }] },
                                        pagination: {
                                            paginationModel: {
                                                pageSize: DEFAULT_PAGE_SIZE, page: 0

                                            }
                                        },
                                    }}
                                    slotProps={{
                                        baseCheckbox: {
                                            checkedIcon: <Box component="img" alt="" src="icons/CheckBox.svg" />,
                                            icon: <CheckBoxOutlineBlankRoundedIcon fontSize="small" />,
                                            sx: {
                                                "&.Mui-checked": { color: '#000000' },
                                                color: '#000000',
                                                fontSize: 20,
                                                "& svg": { fontSize: 20 },
                                            },
                                        },
                                    }}
                                    sx={(theme) => ({
                                        ...getBaseDataGridSx(theme, {
                                            border: 'none',
                                            width: '100%',
                                            // height: '100%',
                                            // minHeight: { xs: 280, sm: 350, md: 400 },
                                            '& .MuiDataGrid-main': {
                                                maxHeight: `calc(100vh - 322px)`,
                                                overflow: 'auto',
                                                width: '100%',
                                                minHeight:"100px"
                                            },
                                            '& .MuiDataGrid-scrollbarFiller': {
                                                borderLeft: '1px solid',
                                                borderColor: '#DEDEDE',
                                                backgroundColor: '#DEDEDE',
                                            },
                                            '& .trail-parent-row': {
                                                backgroundColor: '#FFFFFF',
                                                borderBottom: '1px solid #f0f0f0',
                                                '&:hover': {
                                                    backgroundColor: '#FAFAFA',
                                                },
                                                '& .MuiDataGrid-cell': {
                                                    padding: '12px 8px',
                                                },
                                            },
                                            '& .trail-child-row': {
                                                backgroundColor: '#F8F8F8',
                                                borderBottom: '1px solid #efefef',
                                                borderLeft: '3px solid #E0E0E0',
                                                '&:hover': {
                                                    backgroundColor: '#F0F0F0',
                                                    borderLeftColor: '#BDBDBD',
                                                },
                                                '& .MuiDataGrid-cell': {
                                                    padding: '10px 8px',
                                                    fontSize: '13px',
                                                    color: '#333333',
                                                },
                                            },
                                            '& .trail-empty-child-row': {
                                                backgroundColor: '#F8F8F8',
                                                borderLeft: '3px solid #E0E0E0',
                                            },
                                            '& .trail-empty-child-row .MuiDataGrid-cell': {
                                                color: '#999999',
                                                fontSize: '13px',
                                                fontStyle: 'italic',
                                            },
                                        }),
                                        '& .MuiDataGrid-row[data-rowselectable="false"] .MuiCheckbox-root': { visibility: 'hidden' },
                                    })}
                                    checkboxSelection
                                    rowSelectionModel={selectionModel}
                                    onRowSelectionModelChange={(newModel) => setSelectionModel(newModel)}
                                    isRowSelectable={(params) => getHarvestOptAvailable(params)}


                                />
                            </Box>
                           }
                        </Grid2>

                        <TrialMaterialBulkActions
                            classes={classes}
                            selectionModel={selectionModel}
                            clearBulkSelection={clearBulkSelection}
                            handleMarkAsComplete={handleMarkAsComplete}
                            canMarkSelectedAsComplete={canMarkSelectedAsComplete}
                            isManualCompleteSubmitting={isManualCompleteSubmitting}
                            handleOpenUpdateRemark={handleOpenUpdateRemark}
                            isBulkRemarkSubmitting={isBulkRemarkSubmitting}
                            openRemarkDialog={openRemarkDialog}
                            setOpenRemarkDialog={setOpenRemarkDialog}
                            selectedMaterialIds={selectedMaterialIds}
                            bulkRemarkError={bulkRemarkError}
                            selectedBulkRemark={selectedBulkRemark}
                            setSelectedBulkRemark={setSelectedBulkRemark}
                            setBulkRemarkError={setBulkRemarkError}
                            handleSaveBulkRemark={handleSaveBulkRemark}
                            trialRemarks={trialRemarks}
                        />
                    </Grid2>
                </>
            )}
        </>
    );
}

export default memo(TrailMaterialsDataGrid);
